import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, ShieldCheck } from 'lucide-react';

/**
 * Компонент боковой панели с валидацией пароля
 */
const PasswordValidationSidebar = ({ formData }) => {
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    hasMinLength: false,
    hasLower: false,
    hasUpper: false,
    hasDigit: false,
    hasSpecial: false,
    isCommon: false
  });
  
  // Анализ пароля в реальном времени
  useEffect(() => {
    if (!formData.plaintext || formData.autoGenerate) {
      return;
    }
    
    const password = formData.plaintext;
    
    // Проверки сложности
    const hasMinLength = password.length >= 8;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    // Проверка на распространенные пароли
    const commonPasswords = [
      'password', 'admin', '12345', 'qwerty', '123456', 'welcome', 
      'password123', 'admin123', 'letmein', 'test', 'passw0rd', 
      'P@ssw0rd', 'qwerty123', '000000', '111111', '123123', 
      'пароль', 'админ', 'секрет', 'йцукен'
    ];
    const isCommon = commonPasswords.includes(password.toLowerCase());
    
    // Расчет оценки сложности (0-100)
    let score = 0;
    
    // Базовая оценка по длине
    score += Math.min(password.length * 4, 40);
    
    // Бонусы за разнообразие
    if (hasLower) score += 10;
    if (hasUpper) score += 10;
    if (hasDigit) score += 10;
    if (hasSpecial) score += 20;
    
    // Штраф за повторяющиеся символы
    const repeats = password.length - new Set(password).size;
    score -= repeats * 2;
    
    // Штраф за распространенные пароли
    if (isCommon) score -= 30;
    
    // Ограничение итоговой оценки
    score = Math.max(0, Math.min(100, score));
    
    setPasswordStrength({
      score,
      hasMinLength,
      hasLower,
      hasUpper,
      hasDigit,
      hasSpecial,
      isCommon
    });
  }, [formData.plaintext, formData.autoGenerate]);
  
  // Если включена автогенерация, не показываем валидацию
  if (formData.autoGenerate) {
    return null;
  }

  // Определение уровня безопасности
  const getStrengthLevel = (score) => {
    if (score < 40) return 'Слабый';
    if (score < 70) return 'Средний';
    if (score < 90) return 'Надежный';
    return 'Очень надежный';
  };
  
  // Определение цвета индикатора
  const getStrengthColor = (score) => {
    if (score < 40) return 'red';
    if (score < 70) return 'orange';
    if (score < 90) return 'green';
    return 'blue';
  };
  
  // Если пароль пустой, не показываем валидацию
  if (!formData.plaintext) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ShieldCheck className="w-5 h-5 mr-2 text-blue-500" />
        Оценка надежности пароля
      </h3>

      {/* Индикатор сложности пароля */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Уровень безопасности
          </span>
          <span className={`text-sm font-medium text-${getStrengthColor(passwordStrength.score)}-600 dark:text-${getStrengthColor(passwordStrength.score)}-400`}>
            {getStrengthLevel(passwordStrength.score)}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full bg-${getStrengthColor(passwordStrength.score)}-500`} 
            style={{ width: `${passwordStrength.score}%` }}
          />
        </div>
      </div>

      {/* Критерии безопасности */}
      <div className="space-y-2">
        <div className="flex items-center text-sm">
          {passwordStrength.hasMinLength 
            ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> 
            : <AlertCircle className="w-4 h-4 mr-2 text-red-500" />}
          <span className="text-gray-600 dark:text-gray-400">
            Минимум 8 символов
          </span>
        </div>
        
        <div className="flex items-center text-sm">
          {passwordStrength.hasLower 
            ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> 
            : <AlertCircle className="w-4 h-4 mr-2 text-red-500" />}
          <span className="text-gray-600 dark:text-gray-400">
            Строчные буквы (a-z)
          </span>
        </div>
        
        <div className="flex items-center text-sm">
          {passwordStrength.hasUpper 
            ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> 
            : <AlertCircle className="w-4 h-4 mr-2 text-red-500" />}
          <span className="text-gray-600 dark:text-gray-400">
            Заглавные буквы (A-Z)
          </span>
        </div>
        
        <div className="flex items-center text-sm">
          {passwordStrength.hasDigit 
            ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> 
            : <AlertCircle className="w-4 h-4 mr-2 text-red-500" />}
          <span className="text-gray-600 dark:text-gray-400">
            Цифры (0-9)
          </span>
        </div>
        
        <div className="flex items-center text-sm">
          {passwordStrength.hasSpecial 
            ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> 
            : <AlertCircle className="w-4 h-4 mr-2 text-red-500" />}
          <span className="text-gray-600 dark:text-gray-400">
            Специальные символы (!@#$%)
          </span>
        </div>
        
        {passwordStrength.isCommon && (
          <div className="flex items-center text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-md">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span>
              Это распространенный пароль! Используйте более уникальный вариант.
            </span>
          </div>
        )}
      </div>
      
      {/* Рекомендация */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          Согласно рекомендациям ФСТЭК России, надежный пароль должен включать не менее 8 символов, в том числе буквы в разных регистрах, цифры и специальные символы.
        </p>
      </div>
    </div>
  );
};

export default PasswordValidationSidebar;